import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { Animated, Platform, TouchableOpacity, View } from "react-native";

import { useWebEvent } from "@smartrent/hooks";
import { CheckSolid, Dash } from "@smartrent/icons";
import { hexToRgb } from "@smartrent/utils";

import { useTheme } from "../../utils/theme";

import type { StyleProp, ViewProps, ViewStyle } from "react-native";
import type { TestProps } from "../../types";

export type CheckedType = boolean | "mixed";
export interface CheckboxBaseProps extends TestProps {
  name: string;
  disabled?: boolean;
  checked: CheckedType;
  onChangeChecked: (checked: CheckedType) => void;
  onKeyPress?: (e: any) => void;
  ariaRequired: boolean;
  ariaInvalid: boolean;
  labelID?: string;
  checkboxProps?: ViewProps;
  style?: StyleProp<ViewStyle>;
}

export type CheckboxRef = React.Ref<CheckboxRefProps>;

export interface CheckboxRefProps {
  isIndeterminate: boolean;
  checked: CheckedType;
  setChecked: (checked: CheckedType) => void;
}

export const CheckboxBase = forwardRef(function CheckboxBase(
  {
    name,
    disabled = false,
    checked,
    onKeyPress = () => {
      //
    },
    onChangeChecked,
    ariaRequired,
    ariaInvalid,
    checkboxProps = {},
    labelID,
    testID,
    style,
  }: CheckboxBaseProps,
  ref: CheckboxRef
) {
  const { colors, isLightMode, rounding } = useTheme();

  const { style: checkboxPropsStyle } = checkboxProps;

  const nonNativeDriverState = useRef(new Animated.Value(0)).current;
  const nativeDriverState = useRef(new Animated.Value(0)).current;

  const elementRef = useRef(null);
  useWebEvent(elementRef, "keydown", onKeyPress);

  useEffect(() => {
    Animated.spring(nonNativeDriverState, {
      toValue: checked ? 1 : 0,
      useNativeDriver: false,
    }).start();
    Animated.spring(nativeDriverState, {
      toValue: checked ? 1 : 0,
      useNativeDriver: Platform.OS === "web" ? false : true,
    }).start();
  }, [checked, nativeDriverState, nonNativeDriverState]);

  useImperativeHandle(ref, () => ({
    isIndeterminate: checked === "mixed",
    checked,
    setChecked: (checked: CheckedType) => {
      onChangeChecked(checked);
    },
  }));

  const handlePress = () => {
    if (!disabled) {
      if (checked === "mixed") {
        onChangeChecked(true);
      } else {
        onChangeChecked(!checked);
      }
    }
  };

  return (
    <TouchableOpacity
      disabled={disabled}
      accessible={false} // this prevents this from being tabbed
      onPress={handlePress}
      activeOpacity={0.8}
      style={[{ flexDirection: "row", alignItems: "center" }, style]}
      testID={testID}
      ref={elementRef}
      aria-labelledby={labelID}
    >
      <>
        <Animated.View
          accessible
          accessibilityRole="checkbox"
          accessibilityLiveRegion="assertive"
          accessibilityState={{
            disabled,
            checked,
          }}
          aria-checked={checked}
          aria-required={ariaRequired}
          aria-invalid={ariaInvalid}
          aria-labelledby={`${name}Label`}
          nativeID={name}
          {...checkboxProps}
          style={[
            {
              width: 24,
              height: 24,
              backgroundColor: disabled
                ? isLightMode
                  ? colors.gray200
                  : colors.gray900
                : nonNativeDriverState.interpolate({
                    inputRange: [0, 1],
                    outputRange: [
                      hexToRgb(colors.backgroundContainer) as string,
                      hexToRgb(colors.primary) as string,
                    ],
                  }),
              borderWidth: 1,
              borderColor: disabled
                ? isLightMode
                  ? colors.gray400
                  : colors.gray700
                : nonNativeDriverState.interpolate({
                    inputRange: [0, 1],
                    outputRange: [
                      hexToRgb(
                        isLightMode ? colors.gray300 : colors.gray400
                      ) as string,
                      hexToRgb(colors.primary) as string,
                    ],
                  }),
              borderRadius: rounding.sm,
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            },
            checkboxPropsStyle,
          ]}
        >
          <Animated.View
            accessible={false} // this prevents this from being tabbed
            style={{
              opacity: nativeDriverState.interpolate({
                inputRange: [0, 1],
                outputRange: [0, 1],
              }),
              transform: [
                {
                  translateY: nativeDriverState.interpolate({
                    inputRange: [0, 1],
                    outputRange: [2, 0],
                  }),
                },
              ],
            }}
          >
            {typeof checked === "boolean" && checked === true ? (
              <View
                // this prevents this from being tabbed
                // wrapping Svg in View as the svgs package doesn't properly handle the accessible prop value
                accessible={false}
              >
                <CheckSolid
                  size={20}
                  color={
                    disabled
                      ? isLightMode
                        ? colors.gray400
                        : colors.gray700
                      : isLightMode
                        ? colors.white
                        : colors.gray900
                  }
                />
              </View>
            ) : null}
            {checked === "mixed" ? (
              <View
                accessible={false} // this prevents this from being tabbed
              >
                <Dash
                  size={20}
                  color={
                    disabled
                      ? isLightMode
                        ? colors.gray400
                        : colors.gray700
                      : isLightMode
                        ? colors.white
                        : colors.gray900
                  }
                />
              </View>
            ) : null}
          </Animated.View>
        </Animated.View>
      </>
    </TouchableOpacity>
  );
});
